import { FeatureLabel } from '@/components/marketing/info-tip';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  setDocumentMeta, setCanonical, setOgImage, injectJsonLdList,
  buildWebPageSchema, buildFaqPageSchema, buildBreadcrumbSchema,
} from '@/lib/seo';
import { ContentPage } from '@/components/marketing/content-page';

const FAQS = [
  {
    q: 'What is SLA tracking in a help desk?',
    a: 'SLA (service-level agreement) tracking measures whether your team keeps its response and resolution promises. You set targets — e.g. "respond to High-priority tickets within 1 hour, resolve within 24" — and the help desk runs a countdown on every ticket, warns you before deadlines, and reports your compliance rate. Kora includes SLA tracking free on every workspace.',
  },
  {
    q: 'How much does help desk SLA tracking cost?',
    a: 'In most help desks, SLA tracking is gated to mid-tier plans: Freshdesk requires Growth ($19/agent, limited) or Pro ($55/agent); Zendesk requires Suite Professional ($115/agent); Zoho Desk paid plans start at $7/agent. Kora includes SLA policies, business-hours clocks, pause-on-waiting, breach alerts, and compliance reports free — no per-agent fees.',
  },
  {
    q: 'Do SLA clocks pause when waiting on the customer?',
    a: 'In Kora, yes. When a ticket moves to "Waiting for User" or "Waiting for Team", both SLA clocks pause automatically and resume the moment work continues — so your team is never penalized for time spent waiting on a customer reply.',
  },
  {
    q: 'What are business hours in SLA tracking?',
    a: 'Business hours define when the SLA clock actually runs: your timezone, working days, working hours, and holidays. A 1-hour response promise made at 5 PM on Friday with 9–6 Mon–Fri hours becomes a 9:30 AM Monday deadline. Kora lets each workspace configure its own calendar.',
  },
  {
    q: 'What happens when an SLA is breached?',
    a: 'Kora marks the ticket as breached, alerts the assignee and workspace owners by email, fires a ticket.sla_breach webhook for your integrations, and lists the ticket in a breached-tickets view so it gets prioritized first. Each breach is alerted exactly once — no spam.',
  },
  {
    q: 'Does Kora charge per agent for SLA tracking?',
    a: 'No. Kora has no per-agent pricing at all. SLA tracking — like members, comments, automation, and the knowledge base — is free. You only pay credits for outcomes: 1 credit per ticket created and 1 per AI Analyst answer.',
  },
];

export function SlaTrackingSoftwarePage() {
  useEffect(() => {
    const title = 'SLA Tracking Software — Free, Business Hours | Kora';
    const description = 'SLA tracking for help desks: response and resolution targets, business-hours clocks, pause-on-waiting and breach alerts. Free on every Kora workspace.';
    setDocumentMeta(title, description);
    setCanonical('/sla-tracking-software');
    setOgImage('/assets/og-pricing.png');

    const cleanup = injectJsonLdList([
      buildWebPageSchema('/sla-tracking-software', title, description),
      buildBreadcrumbSchema([
        { name: 'Home', url: 'https://usekora.app/' },
        { name: 'SLA Tracking', url: 'https://usekora.app/sla-tracking-software' },
      ]),
      buildFaqPageSchema(FAQS),
    ]);

    return cleanup;
  }, []);

  return (
    <ContentPage
      pill="Feature guide"
      title="SLA tracking software: keep every response promise"
      subtitle="Set response and resolution targets per priority, run the clocks on your business-hours calendar, pause them while waiting on customers, and know the moment a deadline slips."
      answer={
        <>
          <strong>Kora includes full SLA tracking free on every workspace:</strong> per-priority response and resolution
          targets, business-hours calendars with holidays, automatic pause while waiting on customers, breach alerts
          (email + webhook), and compliance reporting. Competitors gate this behind $19–115/agent plans.
        </>
      }
      ctaTitle="Track your first SLA today"
      ctaSubtitle="Free on every workspace. Pay as you go: $1 = 10 credits, 1 credit per ticket."
    >
      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mt-4 mb-3">
        Why SLA tracking decides help desk evaluations
      </h2>
      <p className="text-sm text-zinc-600 leading-relaxed mb-4">
        A service-level agreement is a promise with a deadline: "Critical tickets get a first response within 30
        minutes." Without tracking, promises live in someone's head and die in a busy queue. With tracking, every
        ticket carries a visible countdown, the team sees amber before red, and leadership gets an honest compliance
        percentage instead of anecdotes.
      </p>
      <p className="text-sm text-zinc-600 leading-relaxed mb-6">
        That's why SLA support is one of the first things buyers screen for — and why most vendors put it behind their
        mid-tier pricing. It's also why Kora ships it free: a promise-tracking feature shouldn't cost more than the
        salaries making the promises.
      </p>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-3">
        What Kora's SLA tracking includes
      </h2>
      <ul className="space-y-2 text-sm text-zinc-600 mb-6 list-disc pl-5">
        <li><strong>Per-priority targets:</strong> first-response and resolution minutes for Critical, High, Medium, and Low — configure only the priorities you promise on.</li>
        <li><strong>Multiple policies:</strong> different rules per category (e.g. tighter SLAs for "Payment Issue"), with the specific policy winning over the default.</li>
        <li><strong>Business-hours calendars:</strong> your timezone, working days, daily hours, and holidays — a 1-hour promise made at 5 PM Friday becomes a 9:30 AM Monday deadline.</li>
        <li><strong>Pause while waiting:</strong> clocks freeze when a ticket waits on the customer or another team, and resume when work continues.</li>
        <li><strong>Breach alerts:</strong> the assignee and owners are notified once per breach, and a <code className="text-xs">ticket.sla_breach</code> webhook fires for your integrations.</li>
        <li><strong>Compliance reporting:</strong> first-response % and resolution % on your dashboard, with a breached-tickets list one click from the queue.</li>
        <li><strong>Everywhere badges:</strong> green → amber → red countdowns on every ticket row and detail view.</li>
      </ul>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-3">
        SLA tracking: Kora vs the per-seat vendors
      </h2>
      <div className="overflow-x-auto rounded-2xl border border-zinc-200 my-6">
        <table className="w-full min-w-[560px] text-sm bg-white ">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50 ">
              <th className="text-left px-4 py-3 font-semibold text-zinc-900 ">Capability</th>
              <th className="text-left px-4 py-3 font-semibold text-zinc-900 ">Kora</th>
              <th className="text-left px-4 py-3 font-semibold text-zinc-900 ">Typical per-seat vendor</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 ">
            {[
              ['SLA policies per priority', 'Free, all workspaces', 'Growth/Professional tiers ($19–115/agent)'],
              ['Business-hours calendars', 'Free, with holidays', 'Mid-tier or Enterprise'],
              ['Pause while waiting on customer', 'Free', 'Often Enterprise-only'],
              ['Breach alerts + webhook', 'Free, once per breach', 'Higher tiers'],
              ['Compliance reporting', 'Free, on every dashboard', 'Mid-tier+'],
              ['Team of 10 monthly cost', 'Credits only (~$0.10/ticket)', '$190–1,150 in seats'],
            ].map(([cap, kora, vendor]) => (
              <tr key={cap}>
                <td className="px-4 py-3 font-medium text-zinc-900 "><FeatureLabel label={cap} /></td>
                <td className="px-4 py-3 text-zinc-600 ">{kora}</td>
                <td className="px-4 py-3 text-zinc-600 ">{vendor}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-3">
        How teams actually use it
      </h2>
      <p className="text-sm text-zinc-600 leading-relaxed mb-4">
        A typical setup: respond to Critical in 30 minutes and High in 60; resolve Critical in 4 hours and High in 24.
        Business hours 9–6 local, Monday to Friday, with public holidays loaded once a year. Agents see countdowns on
        every ticket and work the amber ones before the green. Managers review the compliance percentage weekly — and
        when it dips, the breached-tickets list shows exactly which promises slipped.
      </p>
      <p className="text-sm text-zinc-600 leading-relaxed mb-6">
        Because Kora bills per ticket instead of per agent, better SLAs don't cost you more seats — they just cost
        attention. That alignment is the point.
      </p>

      <p className="text-sm text-zinc-600 mb-6">
        Related: <Link to="/internal-ticketing-software" className="underline text-zinc-900">internal ticketing software</Link>.
      </p>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-4">
        Frequently asked questions
      </h2>
      <div className="divide-y divide-zinc-100 border-y border-zinc-100 mb-8">
        {FAQS.map((faq) => (
          <details key={faq.q} className="group py-3">
            <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-medium text-zinc-900 ">
              {faq.q}
              <span className="text-zinc-400 group-open:rotate-45 transition-transform text-lg leading-none">+</span>
            </summary>
            <p className="mt-2 text-sm text-zinc-500 leading-relaxed">{faq.a}</p>
          </details>
        ))}
      </div>

      <div className="rounded-2xl border border-blue-100 bg-blue-50/50 p-5">
        <p className="text-sm text-zinc-700 leading-relaxed">
          Comparing vendors? See <Link to="/compare/kora-vs-freshdesk" className="font-medium text-blue-600 hover:underline">Kora vs Freshdesk</Link>,{' '}
          <Link to="/compare/kora-vs-zendesk" className="font-medium text-blue-600 hover:underline">Kora vs Zendesk</Link>, or{' '}
          <Link to="/pricing" className="font-medium text-blue-600 hover:underline">how Kora's credits work</Link>.
        </p>
      </div>
    </ContentPage>
  );
}
